import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCampaigns } from '../hooks/useCampaigns';
import { useOrganizations } from '../hooks/useOrganizations';
import { useLanguage } from '../contexts/LanguageContext';
import { getCampaignLang } from '../translations/campaign_lang';
import { Campaign, CampaignType, CampaignAudienceType, CreateCampaignData } from '../types/campaign';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  CalendarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

type Step = 'details' | 'audience' | 'content' | 'schedule' | 'review';

const CampaignBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { user } = useAuth();
  const { language } = useLanguage();
  const tr = getCampaignLang(language);
  const { createCampaign, updateCampaign, campaigns } = useCampaigns();
  const { organizations } = useOrganizations();

  const [step, setStep] = useState<Step>('details');
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState<CampaignType>('email');
  const [audienceType, setAudienceType] = useState<CampaignAudienceType>('all');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [body, setBody] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [sendNow, setSendNow] = useState(true);
  const [scheduledAt, setScheduledAt] = useState('');

  // Load existing campaign if editing
  React.useEffect(() => {
    if (isEdit && campaigns.length > 0) {
      const campaign = campaigns.find(c => c.id === id);
      if (campaign) {
        setName(campaign.name);
        setType(campaign.type);
        setAudienceType(campaign.audience.type);
        setSelectedGroups(campaign.audience.groupIds || []);
        if (campaign.emailContent) {
          setSubject(campaign.emailContent.subject);
          setPreheader(campaign.emailContent.preheader || '');
          setBody(campaign.emailContent.body);
          setFromName(campaign.emailContent.fromName || '');
          setReplyTo(campaign.emailContent.replyTo || '');
        }
        if (campaign.smsContent) {
          setSmsMessage(campaign.smsContent.message);
        }
        setSendNow(campaign.schedule.sendNow);
        if (campaign.schedule.scheduledAt) {
          setScheduledAt(new Date(campaign.schedule.scheduledAt).toISOString().slice(0, 16));
        }
      }
    }
  }, [isEdit, id, campaigns]);

  const steps: { key: Step; label: string; icon: any }[] = [
    { key: 'details', label: tr.campaign.stepDetails, icon: DocumentTextIcon },
    { key: 'audience', label: tr.campaign.stepAudience, icon: UsersIcon },
    { key: 'content', label: tr.campaign.stepContent, icon: EnvelopeIcon },
    { key: 'schedule', label: tr.campaign.stepSchedule, icon: CalendarIcon },
    { key: 'review', label: tr.campaign.stepReview, icon: CheckCircleIcon },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === step);

  const canProceed = (): boolean => {
    switch (step) {
      case 'details': return name.trim().length > 0;
      case 'audience': return true;
      case 'content': return type === 'email' ? subject.trim().length > 0 && body.trim().length > 0 : smsMessage.trim().length > 0;
      case 'schedule': return sendNow || scheduledAt.length > 0;
      default: return true;
    }
  };

  const buildData = (): CreateCampaignData => {
    const data: CreateCampaignData = {
      name,
      type,
      audience: {
        type: audienceType,
        groupIds: audienceType === 'group' ? selectedGroups : undefined,
      },
      schedule: {
        sendNow,
        scheduledAt: !sendNow && scheduledAt ? new Date(scheduledAt) : undefined,
      },
      organizationId: user?.organizationId,
    };

    if (type === 'email') {
      data.emailContent = {
        subject,
        preheader,
        body,
        fromName: fromName || undefined,
        replyTo: replyTo || undefined,
      };
    } else {
      data.smsContent = { message: smsMessage };
    }

    return data;
  };

  const handleSave = async (asDraft: boolean) => {
    setSaving(true);
    try {
      const data = buildData();
      data.status = asDraft ? 'draft' : (sendNow ? 'sending' : 'scheduled');

      if (isEdit && id) {
        await updateCampaign(id, data);
        if (!asDraft) {
          toast.success('Kampány elindítva');
        }
      } else {
        await createCampaign(data);
        if (!asDraft) {
          toast.success('Kampány elindítva');
        }
      }
      navigate('/campaigns');
    } catch (error) {
      console.error('Error saving campaign:', error);
    } finally {
      setSaving(false);
    }
  };

  const audienceTypes: { value: CampaignAudienceType; label: string }[] = [
    { value: 'all', label: tr.campaign.audienceAll },
    { value: 'group', label: tr.campaign.audienceGroup },
    { value: 'filtered', label: tr.campaign.audienceFiltered },
    { value: 'manual', label: tr.campaign.audienceManual },
  ];

  return (
    <div className="space-y-4 max-w-4xl mx-auto px-2 sm:px-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/campaigns')}
            className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {tr.campaign.builderTitle}
          </h1>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        {steps.map((s, idx) => {
          const Icon = s.icon;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    idx <= currentStepIndex
                      ? 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-lg'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
                  }`}
                >
                  {idx < currentStepIndex ? <CheckCircleIcon className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`mt-1 text-xs font-medium hidden sm:block ${
                  idx <= currentStepIndex ? 'text-gray-900 dark:text-white' : 'text-gray-400'
                }`}>
                  {s.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 transition-all ${
                  idx < currentStepIndex ? 'bg-gradient-to-r from-blue-500 to-cyan-500' : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <AnimatePresence mode="wait">
          {/* Step 1: Details */}
          {step === 'details' && (
            <motion.div key="details" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {tr.campaign.campaignName} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={tr.campaign.campaignNamePlaceholder}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {tr.campaign.campaignType}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setType('email')}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      type === 'email'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <EnvelopeIcon className="w-5 h-5" />
                    <span className="font-medium">{tr.campaign.typeEmail}</span>
                  </button>
                  <button
                    onClick={() => setType('sms')}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      type === 'sms'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <ChatBubbleLeftRightIcon className="w-5 h-5" />
                    <span className="font-medium">{tr.campaign.typeSms}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Audience */}
          {step === 'audience' && (
            <motion.div key="audience" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {tr.campaign.audienceType}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {audienceTypes.map((at) => (
                    <button
                      key={at.value}
                      onClick={() => setAudienceType(at.value)}
                      className={`flex items-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all text-left ${
                        audienceType === at.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <UsersIcon className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium text-sm">{at.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {audienceType === 'group' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {tr.campaign.selectGroups}
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {organizations.map((org) => (
                      <label key={org.id} className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedGroups.includes(org.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedGroups([...selectedGroups, org.id]);
                            } else {
                              setSelectedGroups(selectedGroups.filter(g => g !== org.id));
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{org.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Content */}
          {step === 'content' && (
            <motion.div key="content" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              {type === 'email' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {tr.campaign.subject} *
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={tr.campaign.subjectPlaceholder}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {tr.campaign.preheader}
                    </label>
                    <input
                      type="text"
                      value={preheader}
                      onChange={(e) => setPreheader(e.target.value)}
                      placeholder={tr.campaign.preheaderPlaceholder}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {tr.campaign.body} *
                    </label>
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder={tr.campaign.bodyPlaceholder}
                      rows={10}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Támogatott változók: {'{{customer.name}}, {{customer.email}}, {{customer.phone}}'}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {tr.campaign.fromName}
                      </label>
                      <input
                        type="text"
                        value={fromName}
                        onChange={(e) => setFromName(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {tr.campaign.replyTo}
                      </label>
                      <input
                        type="email"
                        value={replyTo}
                        onChange={(e) => setReplyTo(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {tr.campaign.body} *
                  </label>
                  <textarea
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    placeholder="Írd meg az SMS tartalmát..."
                    rows={6}
                    maxLength={160}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">{smsMessage.length}/160</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 4: Schedule */}
          {step === 'schedule' && (
            <motion.div key="schedule" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => setSendNow(true)}
                  className={`flex items-center justify-center space-x-2 px-4 py-4 rounded-lg border-2 transition-all ${
                    sendNow
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  <span className="font-medium">{tr.campaign.sendNow}</span>
                </button>
                <button
                  onClick={() => setSendNow(false)}
                  className={`flex items-center justify-center space-x-2 px-4 py-4 rounded-lg border-2 transition-all ${
                    !sendNow
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <CalendarIcon className="w-5 h-5" />
                  <span className="font-medium">{tr.campaign.scheduleLater}</span>
                </button>
              </div>
              {!sendNow && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {tr.campaign.scheduledAt}
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </motion.div>
          )}

          {/* Step 5: Review */}
          {step === 'review' && (
            <motion.div key="review" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{tr.campaign.reviewTitle}</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{tr.campaign.reviewName}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{tr.campaign.reviewType}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{type === 'email' ? tr.campaign.typeEmail : tr.campaign.typeSms}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{tr.campaign.reviewAudience}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {audienceTypes.find(a => a.value === audienceType)?.label}
                  </span>
                </div>
                {type === 'email' && (
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{tr.campaign.subject}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{subject}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{tr.campaign.reviewSchedule}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {sendNow ? tr.campaign.sendNow : new Date(scheduledAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => currentStepIndex > 0 ? setStep(steps[currentStepIndex - 1].key) : navigate('/campaigns')}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1.5" />
          {currentStepIndex > 0 ? tr.campaign.back : tr.campaign.cancel}
        </button>

        <div className="flex items-center space-x-2">
          {step === 'review' && (
            <>
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {tr.campaign.saveDraft}
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all shadow-sm"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" />
                ) : (
                  <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                )}
                {tr.campaign.send}
              </button>
            </>
          )}
          {step !== 'review' && (
            <button
              onClick={() => setStep(steps[currentStepIndex + 1].key)}
              disabled={!canProceed()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {tr.campaign.next}
              <ArrowRightIcon className="w-4 h-4 ml-1.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignBuilder;
